import { fetchConToken } from '../helpers/fetch';
import { types } from '../types/types';
import { refreshUsuario } from './usuarios';
import { startObtenerVenta } from './ventas';
import {
    ESTADO_PAGADO,
    ESTADO_PENDIENTE,
    MENSAJE_GENERAR_VENTA_RECHAZADA,
    MENSAJE_INICIO_RECHAZADO,
    desenlaceCobro,
    esRevisionManual,
    esperarDesenlaceCobro,
    mensajeInicioCobroRechazado,
} from '../helpers/cobroDatafono';

/**
 * EX-595: cobro con datáfono físico (POSMS). Los endpoints los aporta EX-594.
 *
 * `POST /api/pos/cobro` no crea ninguna venta: manda el importe al terminal. Las ventas las
 * registra el backend al confirmar Paylands, así que el admin espera el desenlace antes de
 * continuar con el flujo de éxito.
 */

const MENSAJE_DATAFONOS_NO_LEIDOS = 'No se ha podido comprobar la configuración de datáfonos';

/**
 * EX-619: terminales que se pueden asociar a una sucursal.
 *
 * `sucursal` es obligatorio salvo en un alta, que todavía no tiene id; `merchant` es el comercio
 * que hay en el formulario, que puede no ser el guardado —es así como la lista se refresca con un
 * UUID recién tecleado, antes de guardar la sucursal—. Los dos viajan juntos siempre que se pueda:
 * hace falta `sucursal` para que el backend resuelva `asociadoA` contra las sucursales de su
 * empresa, y sin él todos los terminales llegarían como libres.
 *
 * Solo los parámetros con valor entran en la query, mismo patrón que `queryCobrosRevision`.
 */
const queryDispositivos = ({ sucursal, merchant } = {}) => {
    const parametros = new URLSearchParams();
    if (sucursal) parametros.set('sucursal', sucursal);
    if (merchant) parametros.set('merchant', merchant);
    const query = parametros.toString();
    return query ? `?${query}` : '';
};

export const startLoadingDispositivosPos = ({ sucursal, merchant } = {}) => {
    return async () => {
        try {
            const resp = await fetchConToken(`pos/dispositivos${queryDispositivos({ sucursal, merchant })}`);
            const body = await resp.json();
            if (!body.ok) {
                return { ok: false, msg: body.msg || 'No se han podido cargar los datáfonos' };
            }
            return { ok: true, dispositivos: body.dispositivos || [], msg: body.msg };
        } catch (error) {
            console.error('Error al cargar los datáfonos:', error);
            return { ok: false, msg: 'No se han podido cargar los datáfonos' };
        }
    };
};

/**
 * EX-611: qué datáfonos tiene asociados cada sucursal de la empresa del token.
 *
 * Es lo que decide si un cobro con tarjeta va al terminal, y por eso NO se lee del store de
 * sucursales: a este lo puebla `POST /api/sucursales/empresa`, que está declarado por encima del
 * `validarJWT` de su router y no puede exponer los identificadores de los terminales.
 *
 * No confundirlo con `startLoadingDispositivosPos`, que pregunta a Paylands por los terminales de
 * la pantalla de configuración: este es una lectura de la propia base de datos y responde igual
 * con el proveedor caído.
 */
export const startLoadingSucursalesDatafonos = () => {
    return async () => {
        try {
            const resp = await fetchConToken('pos/sucursales-datafonos');
            const body = await resp.json();
            if (!body.ok) {
                return { ok: false, msg: body.msg || MENSAJE_DATAFONOS_NO_LEIDOS };
            }
            return { ok: true, sucursales: body.sucursales || [] };
        } catch (error) {
            console.error('Error al comprobar la configuración de datáfonos:', error);
            return { ok: false, msg: MENSAJE_DATAFONOS_NO_LEIDOS };
        }
    };
};

const MENSAJE_COBROS_NO_LEIDOS = 'No se han podido cargar los cobros pendientes de revisión';

/** Solo los parámetros con valor viajan: el backend trata cada filtro como opcional. */
const queryCobrosRevision = ({ sucursal, desde, hasta, incluirResueltos } = {}) => {
    const parametros = new URLSearchParams();
    if (sucursal) parametros.set('sucursal', sucursal);
    if (desde) parametros.set('desde', desde);
    if (hasta) parametros.set('hasta', hasta);
    if (incluirResueltos) parametros.set('incluirResueltos', 'true');
    const query = parametros.toString();
    return query ? `?${query}` : '';
};

/**
 * EX-607: cobros con datáfono que quedaron con la tarjeta cobrada y sin venta registrada.
 *
 * `desde` y `hasta` viajan como `YYYY-MM-DD`: el backend hace `hasta` inclusivo hasta el final de
 * ese día en hora de España, así que mandar un instante ISO con la hora ya aplicada se comería los
 * cobros del último día del rango.
 */
export const startLoadingCobrosRevision = (filtros = {}) => {
    return async () => {
        try {
            const resp = await fetchConToken(`pos/cobros-revision${queryCobrosRevision(filtros)}`);
            const body = await resp.json();
            if (!body.ok) {
                return { ok: false, msg: body.msg || MENSAJE_COBROS_NO_LEIDOS };
            }
            return { ok: true, cobros: body.cobros || [] };
        } catch (error) {
            console.error('Error al cargar los cobros pendientes de revisión:', error);
            return { ok: false, msg: MENSAJE_COBROS_NO_LEIDOS };
        }
    };
};

const MENSAJE_RESOLVER_FALLIDO = 'No se ha podido marcar el cobro como resuelto';

/**
 * Las dos salidas de la revisión manual (EX-606 y EX-612) son el mismo POST sobre el cobro con
 * distinta ruta, el mismo body y la misma constancia, y las dos cambian cuántos quedan sin
 * revisar. Lo único propio de cada una es el mensaje y cómo se lee un fallo.
 *
 * El `status` viaja en el fallo porque el `409` de generar la venta no se distingue por el
 * mensaje, igual que el de iniciar un cobro sobre una venta pendiente (EX-605).
 */
const postAccionCobroRevision = async (dispatch, { ruta, pagoId, nota, msgFallback }) => {
    try {
        const resp = await fetchConToken(`pos/cobro/${pagoId}/${ruta}`, { nota }, 'POST');
        const body = await resp.json();
        if (!body.ok) {
            return { ok: false, status: resp.status, msg: body.msg || msgFallback };
        }
        await dispatch(startRefrescarPendientesRevision());
        return { ok: true, cobro: body.cobro };
    } catch (error) {
        console.error(`${msgFallback}:`, error);
        return { ok: false, msg: msgFallback };
    }
};

/**
 * Deja constancia de que alguien se ha hecho cargo del cobro. NO registra ninguna venta ni
 * devuelve ningún dinero: para registrarla está `startGenerarVentaCobroRevision`.
 */
export const startResolverCobroRevision = (pagoId, nota = '') => {
    return async (dispatch) => postAccionCobroRevision(dispatch, {
        ruta: 'resolver',
        pagoId,
        nota,
        msgFallback: MENSAJE_RESOLVER_FALLIDO,
    });
};

/**
 * EX-612: registra la venta del cobro a partir del payload guardado y, si lo consigue, lo deja
 * resuelto en el mismo paso. El backend revalida stock, saldo y estado de la venta, así que un
 * fallo aquí es normal días después del cobro y deja el cobro exactamente como estaba.
 */
export const startGenerarVentaCobroRevision = (pagoId, nota = '') => {
    return async (dispatch) => postAccionCobroRevision(dispatch, {
        ruta: 'generar-venta',
        pagoId,
        nota,
        msgFallback: MENSAJE_GENERAR_VENTA_RECHAZADA,
    });
};

export const setPendientesRevision = (pendientes) => ({
    type: types.cobrosRevisionPendientesSet,
    payload: pendientes,
});

/**
 * Cuántos cobros quedan sin revisar, para la señal del menú lateral. Se refresca al entrar al
 * panel, al resolver un cobro o generar su venta, y cuando un cobro acaba de terminar en uno de
 * esos estados, que son los momentos en los que el número puede cambiar mientras el panel está
 * abierto.
 *
 * Una lectura fallida deja el contador como estaba: poner un 0 diría "no queda ninguno", que es
 * justo lo contrario de lo que se sabe.
 */
export const startRefrescarPendientesRevision = () => {
    return async (dispatch) => {
        const respuesta = await dispatch(startLoadingCobrosRevision());
        if (!respuesta.ok) return respuesta;

        dispatch(setPendientesRevision(respuesta.cobros.length));
        return respuesta;
    };
};

export const startCobroDatafono = ({ payloadVenta, tipoPayload = 'simple', ventaId, deviceId, descripcion }) => {
    return async () => {
        try {
            const resp = await fetchConToken('pos/cobro', {
                payloadVenta,
                tipoPayload,
                // EX-605: la venta pendiente que este cobro completa. Sin él el backend rechaza
                // el payload de tipo `actualizacion`.
                ...(ventaId && { ventaId }),
                ...(deviceId && { deviceId }),
                ...(descripcion && { descripcion }),
            }, 'POST');
            const body = await resp.json();
            if (!body.ok) {
                // El estado HTTP viaja con el mensaje: el motivo del rechazo se decide con los
                // dos, porque el 400 del backend cubre media docena de causas distintas.
                return { ok: false, msg: body.msg, status: resp.status };
            }
            return { ok: true, pagoId: body.pagoId, orderUuid: body.orderUuid, importe: body.importe };
        } catch (error) {
            console.error('Error al iniciar el cobro con datáfono:', error);
            return { ok: false, msg: MENSAJE_INICIO_RECHAZADO };
        }
    };
};

export const startConsultarCobro = (pagoId) => {
    return async () => {
        try {
            const resp = await fetchConToken(`pos/cobro/${pagoId}`);
            const body = await resp.json();
            if (!body.ok) {
                return { ok: false, msg: body.msg || 'No se ha podido consultar el cobro' };
            }
            return { ok: true, pago: body.pago };
        } catch (error) {
            console.error('Error al consultar el cobro con datáfono:', error);
            return { ok: false, msg: 'No se ha podido consultar el cobro' };
        }
    };
};

/**
 * Registra la intención de cancelar. La cancelación real se hace pulsando Cancelar en el propio
 * terminal: no hay API para abortar una orden pendiente. El cobro se sigue escuchando.
 */
export const startCancelarCobro = (pagoId) => {
    return async () => {
        try {
            const resp = await fetchConToken(`pos/cobro/${pagoId}/cancelar`, {}, 'POST');
            const body = await resp.json();
            if (!body.ok) {
                return { ok: false, msg: body.msg || 'No se ha podido solicitar la cancelación' };
            }
            return { ok: true, estado: body.estado, msg: body.msg };
        } catch (error) {
            console.error('Error al cancelar el cobro con datáfono:', error);
            return { ok: false, msg: 'No se ha podido solicitar la cancelación' };
        }
    };
};

const incluyeMonedero = (payloadVenta = {}) =>
    payloadVenta.metodoPago === 'Monedero'
    || (payloadVenta.pagos || []).some(pago => pago?.metodo === 'Monedero');

/**
 * Cierra un cobro confirmado devolviendo la misma forma que `startSaveVenta`, para que el flujo
 * de éxito del modal de ventas no distinga de dónde vino el pago.
 *
 * La cita se relee del backend porque `GET /api/pos/cobro/:id` solo devuelve los ids de las
 * ventas creadas, no la cita ya actualizada que devuelve `/api/ventas`.
 *
 * EX-605: con `tipoPayload: 'actualizacion'` el cobro completa una venta que ya existía y su id
 * llega en `ventaActualizada`. Se relee poblada porque el modal repuebla el formulario con el
 * documento entero; sin ella daría por perdida la venta que acaba de completarse.
 */
const resolverCobroPagado = (payloadVenta, ventaActualizadaId) => {
    return async (dispatch) => {
        let cita = null;
        let venta = null;

        if (ventaActualizadaId) {
            const respVenta = await dispatch(startObtenerVenta(ventaActualizadaId));
            venta = respVenta?.venta || null;
        }
        if (payloadVenta?.cita && payloadVenta?.empresa) {
            try {
                const resp = await fetchConToken(`citas/cita/${payloadVenta.cita}`, { empresa: payloadVenta.empresa }, 'POST');
                const body = await resp.json();
                cita = body?.cita || null;
            } catch (error) {
                console.error('Error al releer la cita tras el cobro con datáfono:', error);
            }
        }

        // El backend descuenta el monedero al crear las ventas, así que el saldo en Redux se
        // queda atrás igual que se quedaría con `startSaveVentaMultipago`.
        if (incluyeMonedero(payloadVenta) && payloadVenta.usuario && payloadVenta.empresa) {
            try {
                const respUsuario = await fetchConToken('usuarios/usuario', {
                    usuario: payloadVenta.usuario,
                    empresa: payloadVenta.empresa,
                }, 'POST');
                const bodyUsuario = await respUsuario.json();
                if (bodyUsuario.usuario) {
                    dispatch(refreshUsuario(payloadVenta.usuario, bodyUsuario.usuario));
                }
            } catch (error) {
                console.error('Error al refrescar el saldo tras el cobro con datáfono:', error);
            }
        }

        return { ok: true, estado: ESTADO_PAGADO, ...(cita && { cita }), ...(venta && { venta }) };
    };
};

/**
 * Inicia el cobro en el datáfono y espera el desenlace.
 *
 * Resuelve con el contrato de `startSaveVenta` / `startSaveVentaMultipago` (`{ ok, cita? }` o
 * `{ ok: false, error }`) para que el modal de ventas siga tratando la respuesta igual.
 * `permiteReintento: false` marca los desenlaces en los que la tarjeta puede estar ya cobrada y
 * el formulario no debe volver a quedar listo para cobrar.
 *
 * `onEstado` recibe `{ estado, importe, pagoId }` en cada cambio, para el modal de espera.
 */
export const startCobroDatafonoYEsperar = ({ payloadVenta, tipoPayload = 'simple', ventaId, deviceId, descripcion }, { onEstado } = {}) => {
    return async (dispatch) => {
        const inicio = await dispatch(startCobroDatafono({ payloadVenta, tipoPayload, ventaId, deviceId, descripcion }));
        if (!inicio.ok) {
            return {
                ok: false,
                error: mensajeInicioCobroRechazado(inicio),
                permiteReintento: true,
            };
        }

        const { pagoId, importe } = inicio;
        const notificar = (estado) => {
            if (onEstado) onEstado({ estado, importe, pagoId });
        };
        notificar(ESTADO_PENDIENTE);

        const desenlace = await esperarDesenlaceCobro({
            consultar: () => dispatch(startConsultarCobro(pagoId)),
            onEstado: notificar,
        });

        if (desenlace.estado === ESTADO_PAGADO) {
            return dispatch(resolverCobroPagado(payloadVenta, desenlace.ventaActualizada));
        }

        // EX-607: el cobro acaba de sumarse a los que hay que revisar, así que la señal del menú
        // tiene que reflejarlo sin esperar a que el operario recargue el panel.
        if (esRevisionManual(desenlace.estado)) {
            await dispatch(startRefrescarPendientesRevision());
        }

        const mensaje = desenlaceCobro(desenlace.estado, desenlace.detalleError);
        return {
            ok: false,
            estado: desenlace.estado,
            detalleError: desenlace.detalleError,
            error: mensaje.texto,
            permiteReintento: mensaje.permiteReintento,
        };
    };
};
